import React, { useState, useEffect } from 'react';
import { CloseIcon, UploadCloudIcon, CautionIcon, PlusIcon } from '../../../../public/icons';
import { useToast } from '../../../../context/ToastContext';
import adminCompatibleService from '../../../../services/admin/admin-addonCompatibleService';
import axios from 'axios';

const AddAddonModal = ({ isOpen, onClose, onSubmit, loading = false, hideBaseProduct = false }) => {
  const [variants, setVariants] = useState([]);
  const [currentVariant, setCurrentVariant] = useState({
    name: '',
    price: '',
    stockQuantity: '',
    image: null,
    imagePreview: null,
    description: ''
  });
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [formData, setFormData] = useState({
    baseProduct: '',
    baseProductType: '',
    name: '',
    basePrice: '',
    shortDescription: '',
    isFunctional: false,
    contentType: 'audio',
    status: 'active'
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [compatibleItems, setCompatibleItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const { error, success: showSuccess } = useToast();

  // Fetch compatible items when modal opens
  useEffect(() => {
    if (isOpen && !hideBaseProduct) {
      fetchCompatibleItems();
    }
  }, [isOpen, hideBaseProduct]);

  const fetchCompatibleItems = async () => {
    try {
      setLoadingItems(true);
      console.log('🔄 [AddAddonModal] Fetching compatible items...');
      
      const response = await adminCompatibleService.getCompatibleItems();
      
      if (response.success) {
        console.log('✅ [AddAddonModal] Compatible items fetched:', response.data.items.length);
        setCompatibleItems(response.data.items);
      } else {
        error('Failed to fetch compatible items');
      }
    } catch (err) {
      console.error('💥 [AddAddonModal] Error fetching compatible items:', err);
      error('Failed to fetch compatible items');
    } finally {
      setLoadingItems(false);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        baseProduct: '',
        baseProductType: '',
        name: '',
        basePrice: '',
        shortDescription: '',
        isFunctional: false,
        contentType: 'audio',
        status: 'active'
      });
      setVariants([]);
      setCurrentVariant({
        name: '',
        price: '',
        stockQuantity: '',
        image: null,
        imagePreview: null,
        description: ''
      });
      setEditingVariantId(null);
    }
  }, [isOpen]);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      variants.forEach(variant => {
        if (variant.imagePreview) {
          URL.revokeObjectURL(variant.imagePreview);
        }
      });
      if (currentVariant.imagePreview) {
        URL.revokeObjectURL(currentVariant.imagePreview);
      }
    };
  }, [variants, currentVariant.imagePreview]);

  if (!isOpen) return null;

  // Upload image to Cloudinary
  const uploadToCloudinary = async (file) => {
    const cloudinaryFormData = new FormData();
    cloudinaryFormData.append('file', file);
    cloudinaryFormData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
      cloudinaryFormData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      }
    );
    
    return {
      url: response.data.secure_url,
      publicId: response.data.public_id
    };
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle base product selection with type
    if (name === 'baseProduct') {
      const [type, id] = value.split('|');
      setFormData(prev => ({
        ...prev,
        baseProduct: id,
        baseProductType: type
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleVariantChange = (e) => {
    const { name, value } = e.target;
    setCurrentVariant(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVariantImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        error('Image size should be less than 5MB');
        return;
      }
      
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        error('Only JPG, PNG, and WebP images are allowed');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      
      setCurrentVariant(prev => ({
        ...prev,
        image: file,
        imagePreview: previewUrl
      }));

      setUploadingImage(true);
      try {
        const cloudinaryResponse = await uploadToCloudinary(file);
        setCurrentVariant(prev => ({
          ...prev,
          image: cloudinaryResponse,
          imagePreview: previewUrl
        }));
        showSuccess('Variant image uploaded successfully');
      } catch (uploadError) {
        console.error('Image upload error:', uploadError);
        error('Failed to upload image. Please try again.');
        setCurrentVariant(prev => ({
          ...prev,
          image: null,
          imagePreview: null
        }));
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const addVariant = () => {
    if (!currentVariant.name || !currentVariant.price) {
      error('Variant name and price are required');
      return;
    }

    if (currentVariant.price <= 0) {
      error('Price must be greater than 0');
      return;
    }

    if (currentVariant.stockQuantity && currentVariant.stockQuantity < 0) {
      error('Stock quantity cannot be negative');
      return;
    }

    const newVariant = {
      ...currentVariant,
      id: Date.now(),
      price: parseFloat(currentVariant.price),
      stockQuantity: currentVariant.stockQuantity ? parseInt(currentVariant.stockQuantity) : 0
    };

    if (editingVariantId) {
      setVariants(prev => prev.map(variant => 
        variant.id === editingVariantId ? { ...newVariant, id: editingVariantId } : variant
      ));
      setEditingVariantId(null);
    } else {
      setVariants(prev => [...prev, newVariant]);
    }

    setCurrentVariant({
      name: '',
      price: '',
      stockQuantity: '',
      image: null,
      imagePreview: null,
      description: ''
    });
  };

  const editVariant = (variant) => {
    setCurrentVariant({
      name: variant.name,
      price: variant.price.toString(),
      stockQuantity: variant.stockQuantity?.toString() || '',
      image: variant.image || null,
      imagePreview: variant.imagePreview || null,
      description: variant.description || ''
    });
    setEditingVariantId(variant.id);
  };

  const removeVariant = (id) => {
    const variant = variants.find(v => v.id === id);
    if (variant?.imagePreview) {
      URL.revokeObjectURL(variant.imagePreview);
    }
    setVariants(prev => prev.filter(variant => variant.id !== id));
  };

  const cancelEdit = () => {
    if (currentVariant.imagePreview) {
      URL.revokeObjectURL(currentVariant.imagePreview);
    }
    setCurrentVariant({
      name: '',
      price: '',
      stockQuantity: '',
      image: null,
      imagePreview: null,
      description: ''
    });
    setEditingVariantId(null);
  };

  const handleSubmit = () => {
    // Validate form data
    if (!hideBaseProduct && !formData.baseProduct) {
      error('Please select a base product');
      return;
    }

    if (!formData.name.trim()) {
      error('Add-on name is required');
      return;
    }

    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
      error('Valid base price is required');
      return;
    }

    // For functional add-ons, validate variants
    if (formData.isFunctional && variants.length === 0) {
      error('Functional add-ons require at least one variant');
      return;
    }

    const addonData = {
      name: formData.name.trim(),
      basePrice: parseFloat(formData.basePrice),
      shortDescription: formData.shortDescription.trim() || '',
      isFunctional: formData.isFunctional,
      contentType: formData.isFunctional ? formData.contentType : 'none',
      status: formData.status,
      variants: variants.map(variant => ({
        name: variant.name.trim(),
        price: variant.price,
        stockQuantity: variant.stockQuantity || 0,
        description: variant.description?.trim() || '',
        image: variant.image
      }))
    };

    // Include base product info if not hidden
    if (!hideBaseProduct && formData.baseProduct) {
      const selectedItem = compatibleItems.find(
        item => item.id === formData.baseProduct && item.type === formData.baseProductType
      );
      
      addonData.baseProductInfo = {
        id: formData.baseProduct,
        type: formData.baseProductType,
        name: selectedItem?.name || ''
      };
    }

    console.log('📤 [AddAddonModal] Submitting addon data:', JSON.stringify(addonData, null, 2));
    onSubmit(addonData);
  };

  const isSubmitEnabled = (hideBaseProduct || formData.baseProduct) && 
                          formData.name.trim() && 
                          formData.basePrice && 
                          parseFloat(formData.basePrice) > 0;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] text-left lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b mx-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">Add-on Details</h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Basic information about this optional product.
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] disabled:opacity-50"
              disabled={loading || uploadingImage}
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Base Product Dropdown - Only show if not hidden */}
            {!hideBaseProduct && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                  Base Product/Collection *
                </label>
                {loadingItems ? (
                  <div className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] flex items-center justify-center">
                    <div className="animate-spin  h-5 w-5 border-b-2 border-[#55000C] mr-2"></div>
                    <span className="text-sm text-gray-500">Loading items...</span>
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      name="baseProduct"
                      value={formData.baseProduct ? `${formData.baseProductType}|${formData.baseProduct}` : ''}
                      onChange={handleChange}
                      className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] appearance-none focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50 pr-10"
                      disabled={loading || uploadingImage}
                    >
                      <option value="">Select a product or collection</option>
                      <optgroup label="Standalone Products">
                        {compatibleItems
                          .filter(item => item.type === 'product')
                          .map(product => (
                            <option key={`product|${product.id}`} value={`product|${product.id}`}>
                              {product.name} (Product)
                            </option>
                          ))}
                      </optgroup>
                      <optgroup label="Standalone Collections">
                        {compatibleItems
                          .filter(item => item.type === 'collection')
                          .map(collection => (
                            <option key={`collection|${collection.id}`} value={`collection|${collection.id}`}>
                              {collection.name} (Collection)
                            </option>
                          ))}
                      </optgroup>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                      <svg className="w-5 h-5 text-[#6B635D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                )}
                <p className="mt-2 text-sm text-gray-600">
                  Select the standalone product or collection this add-on will be associated with.
                </p>
              </div>
            )}
            
            {/* Add-on name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Add-on name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter add-on name"
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                disabled={loading || uploadingImage}
              />
            </div>

            {/* Base price */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Base price (₦) *
              </label>
              <input
                type="number"
                name="basePrice"
                value={formData.basePrice}
                onChange={handleChange}
                placeholder="15000"
                min="0"
                step="0.01"
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                disabled={loading || uploadingImage}
              />
            </div>

            {/* Short description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Short description
              </label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                rows={3}
                placeholder="Enter description..."
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none disabled:opacity-50"
                disabled={loading || uploadingImage}
              />
              <p className="mt-2 text-sm text-gray-600">
                Keep this short and emotional. This appears in the gift customization flow.
              </p>
            </div>

            {/* Status Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Status *
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] appearance-none focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50 pr-10"
                  disabled={loading || uploadingImage}
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                  <svg className="w-5 h-5 text-[#6B635D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Set the initial status for this add-on.
              </p>
            </div>

            {/* Functional add-on checkbox */}
            <div className="mb-6">
              <div className="flex items-start">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="isFunctional"
                    name="isFunctional"
                    checked={formData.isFunctional}
                    onChange={handleChange}
                    disabled={loading || uploadingImage}
                    className="sr-only peer"
                  />
                  <div className={`w-6 h-6 border-2 border-[#1C2A46] rounded peer-checked:bg-[#1C2A46] peer-checked:border-[#1C2A46] flex items-center justify-center ${
                    loading || uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                    {formData.isFunctional && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </label>
                <div className="ml-2">
                  <label htmlFor="isFunctional" className="block text-sm font-medium text-[#1C2A46] cursor-pointer">
                    Is this a functional add-on
                  </label>
                  <p className="text-sm text-gray-600">
                    Functional add-ons require user-uploaded content (e.g. audio or video) and can have multiple variants.
                  </p>
                </div>
              </div>
            </div>

            {/* Content type - Only show if isFunctional is true */}
            {formData.isFunctional && (
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                  Content type
                </label>
                <select
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                  disabled={loading || uploadingImage}
                >
                  <option value="audio">Audio</option>
                  <option value="video">Video</option>
                  <option value="image">Image</option>
                  <option value="text">Text</option>
                </select>
                <p className="mt-2 text-sm text-gray-600">
                  Determines what customers can upload later.
                </p>
              </div>
            )}

            {/* Variants Section - Only show if isFunctional is true */}
            {formData.isFunctional && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-[#1C2A46]">Add-on Variants</h3>
                    <p className="text-sm text-gray-600">
                      Variants are the physical forms this add-on can take.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {editingVariantId && (
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                        disabled={uploadingImage}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={addVariant}
                      disabled={!currentVariant.name || !currentVariant.price || loading || uploadingImage}
                      className="flex items-center gap-2 px-4 py-2 text-[#55000C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="w-5 h-5" color='#55000C' />
                      {editingVariantId ? 'Update' : 'Add'}
                    </button>
                  </div>
                </div>
                
                {/* Variants List */}
                {variants.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {variants.map(variant => (
                      <div key={variant.id} className="flex items-center justify-between ">
                        <div className="flex items-center gap-4">
                          {variant.image?.url ? (
                            <div className="h-20 w-20  overflow-hidden border border-[#DBDBDB]">
                              <img 
                                src={variant.image.url} 
                                alt={variant.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : variant.imagePreview ? (
                            <div className="h-20 w-20  overflow-hidden border border-[#DBDBDB]">
                              <img 
                                src={variant.imagePreview} 
                                alt={variant.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="h-16 w-16 bg-[#F5F5F5]  border border-[#DBDBDB] flex items-center justify-center">
                              <span className="text-xs text-gray-400">No image</span>
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-[#1C2A46]">{variant.name}</h4>
                            <p className="text-sm text-gray-600">₦{variant.price.toLocaleString()}</p>
                            {variant.stockQuantity > 0 && (
                              <p className="text-xs text-gray-500">Stock: {variant.stockQuantity}</p>
                            )}
                           
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => editVariant(variant)}
                            className="text-sm font-medium underline text-[#1C2A46] hover:text-[#D2A146] transition-colors disabled:opacity-50"
                            disabled={loading || uploadingImage}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeVariant(variant.id)}
                            className="text-sm font-medium underline text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                            disabled={loading || uploadingImage}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Variant Form */}
                <div className="space-y-4 ">
                  <div>
                    <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                      Variant name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={currentVariant.name}
                      onChange={handleVariantChange}
                      placeholder="Enter variant name"
                      className="w-full p-3 bg-white border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                      disabled={loading || uploadingImage}
                    />
                  </div>

                  <div className='flex justify-between gap-3'>
                    <div className='w-1/2'>
                      <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                        Price (₦) *
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={currentVariant.price}
                        onChange={handleVariantChange}
                        placeholder="15000"
                        min="0"
                        step="0.01"
                        className="w-full p-3 bg-white border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                        disabled={loading || uploadingImage}
                      />
                    </div>

                    <div className='w-1/2'>
                      <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                        Stock quantity
                      </label>
                      <input
                        type="number"
                        name="stockQuantity"
                        value={currentVariant.stockQuantity}
                        onChange={handleVariantChange}
                        placeholder="100"
                        min="0"
                        className="w-full p-3 bg-white border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                        disabled={loading || uploadingImage}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                      Variant description
                    </label>
                    <textarea
                      name="description"
                      value={currentVariant.description}
                      onChange={handleVariantChange}
                      rows={2}
                      placeholder="Enter variant description..."
                      className="w-full p-3 bg-white border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none disabled:opacity-50"
                      disabled={loading || uploadingImage}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                      Variant Image
                    </label>
                    
                    {currentVariant.imagePreview ? (
                      <div className="mb-4">
                        <div className="relative h-32 w-32  overflow-hidden border border-gray-200">
                          <img 
                            src={currentVariant.imagePreview} 
                            alt="Preview" 
                            className="h-full w-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (currentVariant.imagePreview) {
                                URL.revokeObjectURL(currentVariant.imagePreview);
                              }
                              setCurrentVariant(prev => ({
                                ...prev,
                                image: null,
                                imagePreview: null
                              }));
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-600 text-white  hover:bg-red-700 text-xs"
                            disabled={uploadingImage}
                          >
                            ✕
                          </button>
                          {uploadingImage && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center ">
                              <div className="animate-spin  h-8 w-8 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>
                        <p className="mt-2 text-sm text-green-600">
                          {uploadingImage ? 'Uploading image...' : 'Image uploaded'}
                        </p>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-[#DBDBDB] bg-white p-6 text-center hover:border-[#D2A146] transition-colors cursor-pointer ">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleVariantImageChange}
                          className="hidden"
                          id="variantImage"
                          disabled={loading || uploadingImage}
                        />
                        <label htmlFor="variantImage" className={`cursor-pointer ${loading || uploadingImage ? 'opacity-50' : ''}`}>
                          <div className="flex justify-center items-center mb-3">
                            <UploadCloudIcon className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-md font-medium text-gray-700 mb-2">
                            {uploadingImage ? 'Uploading...' : 'Click to upload or drag and drop'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Recommended size: 1200 × 800px.<br />
                            Max file size: 5MB
                          </p>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Information note for non-functional add-ons */}
            {!formData.isFunctional && (
              <div className="mb-8 p-4 bg-blue-50 border border-blue-200 ">
                <div className="flex items-start gap-3">
                  <CautionIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800">
                      <strong>Non-functional add-on</strong>
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      This add-on doesn't require variants. It will be available as a single option with the base price.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#DBDBDB]">
            <button 
              onClick={handleSubmit}
              disabled={!isSubmitEnabled || loading || uploadingImage}
              className={`w-full px-6 py-3 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isSubmitEnabled && !loading && !uploadingImage
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500'
              }`}
            >
              {loading ? 'Creating...' : uploadingImage ? 'Uploading...' : 'Create Add-on'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAddonModal;